import { createContext, useContext, type DragEvent } from 'react';
import type { CashFlowEntry, MoneyMode } from '@/lib/cashFlow';
import type { CalendarModel, HoverPos, PlacedEntry } from './model';

/**
 * Shared calendar context. CalendarGrid owns all state (mode, lens, drag, hover,
 * the model) and exposes it here so the four granularity layouts and the leaf
 * DayCell / CalendarChip render without prop-drilling. Read-only consumers; every
 * mutation routes back through the orchestrator's handlers.
 */
export interface CalendarCtxValue {
  mode: MoneyMode;
  /** Shared "rõhuta prognoos" toggle — washes forecast chips amber (L14 shared layer). */
  forecastEmphasis: boolean;
  /** True when the shared reporting basis is accrual (Tekkepõhine): document-date
   *  placement. Running-balance affordances (a CASH truth — the LHV-anchored bank
   *  balance) hide, and drag-to-reschedule is disabled (expected dates don't place
   *  chips there, so a drag would look like a no-op). L14 shared layer. */
  accrual: boolean;
  today: string;
  model: CalendarModel;
  /** Apply the active lens (sort + only-unpaid) to a day's entries for display. */
  arrangeDay: (list: PlacedEntry[]) => PlacedEntry[];

  /** The entry currently being dragged (null when idle), and the hovered day. */
  drag: CashFlowEntry | null;
  overDate: string | null;
  startDrag: (e: CashFlowEntry) => void;
  endDrag: () => void;
  dayDragOver: (date: string, ev: DragEvent) => void;
  dayDragLeave: (date: string) => void;
  dayDrop: (date: string, ev: DragEvent) => void;

  /** Open a floating hover card for a grouped (installment) or single entry. */
  showGroupHover: (pos: HoverPos | null) => void;
  showDocHover: (pos: HoverPos | null) => void;

  /** Open the day register overlay; quick-add a new entry on a day. */
  openDay: (date: string, net: number) => void;
  addEntry: (date: string) => void;

  /** Drill the register, scoped to ONE carried-forward summary group's EXACT
   *  folded ids — the same scoped Ledger the table opens (F1 / L14). */
  openCarriedForward: (kind: 'income' | 'cost', ids: string[]) => void;

  /** Open the shared enriched-entry / document detail overlay for ONE entry — the
   *  SAME drill surface the table lens opens (L14: a capability reachable from BOTH
   *  lenses, never calendar-only). Day-click still drills to the register; this is
   *  the per-document direct-open affordance. */
  openEntry: (id: string) => void;
}

const CalendarContext = createContext<CalendarCtxValue | null>(null);

export const CalendarProvider = CalendarContext.Provider;

export function useCalendar(): CalendarCtxValue {
  const ctx = useContext(CalendarContext);
  if (!ctx) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return ctx;
}
